import { faGear } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Tooltip, Typography } from "@mui/material"
import { IAgency } from "agency/model"
import { getAgencyNameById } from "agency/tools/common"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { NotificationSliderIcon } from "notification/components/NotificationSliderIcon"
import { SETTINGS_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { connect } from "react-redux"
import { MAIN_LAYOUT_USER_MENU_BUTTON_ID, MENU_OPENED_WIDTH_IN_REM, USER_MENU_BUTTON_HEIGHT_IN_REM } from "ui/model"
import { addImportant } from "ui/tools/common"
import { setMenuExpansion } from "ui/tools/menu"
import { getUserDisplayName } from "user/tools/common"
import { canViewAgencyPage } from "user/tools/permissions"
import { FlexBox } from "../../ui/components/FlexBox"
import { UserActionButtons } from "./UserActionButtons"

interface IUserMenuPanelProps {
  sx?: any
}

interface IUserMenuPanelConnectProps {
  isMenuOpened: boolean
  isNotificationSliderOpened: boolean
  agencies: IAgency[] // used only to refresh the UI
  agencyId: number | null
  agencyName: string
  userName: string
  userEmail: string
}

const UserMenuPanelFn = React.memo(
  ({
    userName,
    agencyId,
    isNotificationSliderOpened,
    isMenuOpened,
    agencyName,
    sx
  }: IUserMenuPanelProps & IUserMenuPanelConnectProps) => {
    const canViewAgency = canViewAgencyPage(agencyId)
    const isOpened = isMenuOpened && !isNotificationSliderOpened
    const SettingsIcon = (
      <Tooltip title={translate("settings.display")}>
        <span>
          <FontAwesomeIcon
            icon={faGear}
            size="2x"
            className="settings-icon"
            onClick={() => {
              if (canViewAgency) {
                navigate({ path: SETTINGS_PATH, pathParams: "profile" })
              } else if (!isMenuOpened && !isNotificationSliderOpened) {
                setMenuExpansion(!isMenuOpened)
              }
            }}
          />
        </span>
      </Tooltip>
    )
    if (!isOpened) {
      return (
        <FlexBox
          fullWidth
          vertical
          gap
          sx={{
            "p": "0.25rem",
            "width": "calc(100% - 2rem)",
            "mb": "1rem",
            "borderRadius": "0.25rem",
            "& .settings-icon, & .notification-slider-icon": {
              color: theme => theme.palette.text.primary2,
              cursor: "pointer"
            },
            "& .settings-icon:hover *,  & .notification-slider-icon:hover *": {
              color: theme => addImportant(theme.palette.primary.main as string)
            },
            ...sx
          }}>
          <NotificationSliderIcon />
          {SettingsIcon}
        </FlexBox>
      )
    }
    return (
      <FlexBox
        vertical
        id="user-menu-panel"
        sx={{
          "minWidth": "100%",
          "maxWidth": "100%",
          "backgroundColor": theme => theme.palette.background.paper,
          "& .settings-icon": {
            color: theme => theme.palette.text.primary2,
            cursor: "pointer"
          },
          "& .settings-icon:hover *": {
            color: theme => addImportant(theme.palette.primary.main as string)
          },
          "& .user-action-buttons": {
            pl: "1rem",
            pr: "1rem"
          },
          ...sx
        }}>
        <UserActionButtons />
        <FlexBox
          jc="flex-start"
          gap
          id={MAIN_LAYOUT_USER_MENU_BUTTON_ID}
          sx={{
            "width": "calc(100% - 2rem)",
            "minWidth": "calc(100% - 2rem)",
            "maxWidth": "calc(100% - 2rem)",
            "p": "0",
            "pl": ".5rem",
            "pr": ".5rem",
            "height": USER_MENU_BUTTON_HEIGHT_IN_REM,
            "minHeight": USER_MENU_BUTTON_HEIGHT_IN_REM,
            "maxHeight": USER_MENU_BUTTON_HEIGHT_IN_REM,
            "borderRadius": ".25rem",
            "& .icon": {
              cursor: "pointer",
              color: theme => theme.palette.text.primary2,
              fontSize: "1.5rem"
            }
          }}>
          {SettingsIcon}
          <FlexBox
            vertical
            sx={{
              maxWidth: `calc(${MENU_OPENED_WIDTH_IN_REM} - 7rem)`,
              cursor: canViewAgency ? "pointer" : undefined
            }}>
            <Typography
              id="main-layout-user-menu-username"
              sx={{
                width: "100%",
                minWidth: "100%",
                maxWidth: "100%",
                fontSize: "0.85rem",
                fontWeight: "500",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: theme => theme.palette.text.primary2
              }}
              onClick={canViewAgency ? () => navigate({ path: SETTINGS_PATH, pathParams: "profile" }) : undefined}>
              {userName}
            </Typography>
            <Typography
              id="main-layout-user-menu-agency-name"
              sx={{
                width: "100%",
                minWidth: "100%",
                maxWidth: "100%",
                fontSize: ".8rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: theme => theme.palette.primary.main
              }}
              onClick={canViewAgency ? () => navigate({ path: SETTINGS_PATH, pathParams: "agency" }) : undefined}>
              {agencyName}
            </Typography>
          </FlexBox>
        </FlexBox>
      </FlexBox>
    )
  }
)

export const UserMenuPanel = connect((state: IRootState): IUserMenuPanelConnectProps => {
  const agencyName =
    state.user.user.agency_id === null ? translate("user.no.agency") : getAgencyNameById(state.user.user.agency_id)
  return {
    isMenuOpened: state.ui.isMenuOpened,
    isNotificationSliderOpened: state.notification.isSliderOpened,
    userName: getUserDisplayName(state.user),
    agencyId: state.user.user.agency_id,
    userEmail: state.user.user.email || "",
    agencies: state.agency.agencies,
    agencyName: !agencyName ? translate("label.agency") : agencyName
  }
})(UserMenuPanelFn)
